import {
  GAME_SESSION_STATUS,
  WRONG_MOVE_HANDLING,
  type CurrentSessionResponse,
  type GameOverRequest,
  type GameOverResponse,
  type GameSessionStatus,
  type GameStartRequest,
  type GameStartResponse,
  type NextSequenceRequest,
  type NextSequenceResponse,
  type ValidateRequest,
  type ValidateResponse,
} from '@/types/api.types';

import gameConfigData from '../data/game-config.json';

import { calculateFinalScore, calculateTimeBonus, isBonusEligible } from './score-engine';
import { generateSequence } from './sequence';

export type BoardDifficulty = 'easy' | 'medium' | 'hard';

const DIFFICULTY_ORDER: BoardDifficulty[] = ['easy', 'medium', 'hard'];

export interface GameEngineState {
  sessionId: string;
  status: GameSessionStatus;
  startedAtMs: number;
  expiryAtMs: number;
  completedRounds: number;
  successfulRounds: number;
  successfulMoves: number;
  currentRound: number;
  currentActualRound: number;
  actualRoundRequested: number;
  hasAnyPreviousWrong: boolean;
  hasAnySkip: boolean;
  skippedRounds: number;
  lastMoveAtMs: number | null;
  fullSequence: number[];
  enableTimer: boolean;
  timeLimitSeconds: number;
  baseDifficulty: BoardDifficulty;
  boardCountFromUrl?: number;
}

export class GameEngine {
  private state: GameEngineState | null = null;
  private config = gameConfigData;

  public get isActive(): boolean {
    return (
      this.state !== null &&
      (this.state.status === GAME_SESSION_STATUS.STARTED ||
        this.state.status === GAME_SESSION_STATUS.RESULT_PROCESSING)
    );
  }

  public getState(): GameEngineState | null {
    return this.state;
  }

  private getActiveLevelConfig(currentRound: number) {
    const baseDifficulty = this.state?.baseDifficulty ?? 'easy';
    const boardCountFromUrl = this.state?.boardCountFromUrl;
    const levels = this.config.levels;

    const startIndex = DIFFICULTY_ORDER.indexOf(baseDifficulty);

    let roundsCounted = 0;
    for (let i = startIndex; i < DIFFICULTY_ORDER.length; i++) {
      const diff = DIFFICULTY_ORDER[i];
      const conf = levels[diff];
      const activeBoardCount = boardCountFromUrl ?? conf.boardCount;

      if (currentRound <= roundsCounted + activeBoardCount) {
        return {
          difficulty: diff,
          config: conf,
          relativeRound: Math.max(1, currentRound - roundsCounted),
          activeBoardCount,
        };
      }
      roundsCounted += activeBoardCount;
    }

    const hardConf = levels['hard'];
    const activeBoardCount = boardCountFromUrl ?? hardConf.boardCount;
    return {
      difficulty: 'hard' as BoardDifficulty,
      config: hardConf,
      relativeRound: Math.max(1, currentRound - roundsCounted),
      activeBoardCount,
    };
  }

  public getTotalRounds() {
    const baseDifficulty = this.state?.baseDifficulty ?? 'easy';
    const boardCountFromUrl = this.state?.boardCountFromUrl;
    const levels = this.config.levels as Record<BoardDifficulty, { boardCount: number }>;
    const startIndex = DIFFICULTY_ORDER.indexOf(baseDifficulty);
    let total = 0;
    for (let i = startIndex; i < DIFFICULTY_ORDER.length; i++) {
      const diff = DIFFICULTY_ORDER[i];
      const activeBoardCount = boardCountFromUrl ?? levels[diff].boardCount;
      total += activeBoardCount;
    }
    return total;
  }

  public getConfig() {
    return {
      ...this.config,
      totalRounds: this.state ? this.getTotalRounds() : 15,
      totalDemoRounds: this.config.demoMaxSequence - this.config.demoMinSequence + 1,
    };
  }

  public startSession(
    req: GameStartRequest & {
      enableTimer?: boolean;
      timeLimit?: number;
      difficulty?: BoardDifficulty;
      boardCount?: number;
    },
  ): Promise<GameStartResponse> {
    const now = Date.now();
    const enableTimer = req.enableTimer ?? true;
    const timeLimitSeconds = req.timeLimit ?? this.config.timeLimit;
    const timeLimitMs = timeLimitSeconds * 1000;

    const sessionId = crypto.randomUUID();
    const baseDifficulty = req.difficulty ?? 'easy';

    // Absolute max sequence is always 15 across all levels to avoid bounds issues
    const fullSequence = generateSequence(15, this.config.cellCount);

    this.state = {
      sessionId,
      status: GAME_SESSION_STATUS.STARTED,
      startedAtMs: now,
      expiryAtMs: enableTimer ? now + timeLimitMs : Number.MAX_SAFE_INTEGER,
      completedRounds: 0,
      successfulRounds: 0,
      successfulMoves: 0,
      currentRound: 1,
      currentActualRound: 1,
      actualRoundRequested: 1,
      hasAnyPreviousWrong: false,
      hasAnySkip: false,
      skippedRounds: 0,
      lastMoveAtMs: null,
      fullSequence,
      enableTimer,
      timeLimitSeconds,
      baseDifficulty,
      boardCountFromUrl: req.boardCount,
    };

    const activeInfo = this.getActiveLevelConfig(1);
    const seqLength = activeInfo.config.minSequence;
    const sequence = fullSequence.slice(0, seqLength);

    return Promise.resolve({
      sessionId,
      gameSessionId: sessionId,
      isDemo: false,
      isDemoMode: false,
      currentRound: 1,
      currentActualRound: 1,
      sequence,
      endTime: enableTimer ? new Date(this.state.expiryAtMs).toISOString() : null,
      timeDelay: this.config.levelDelay,
      flashDelay: this.config.flashDelay,
      isResumed: false,
      status: GAME_SESSION_STATUS.STARTED,
      isCompleted: false,
    });
  }

  public validateMove(req: ValidateRequest): Promise<ValidateResponse> {
    if (!this.state) {
      return Promise.resolve({ score: 0, isFlagged: false });
    }

    const now = Date.now();
    this.state.lastMoveAtMs = now;

    const activeInfo = this.getActiveLevelConfig(this.state.currentActualRound);
    const seqLength = Math.min(
      activeInfo.config.maxSequence,
      activeInfo.config.minSequence + (activeInfo.relativeRound - 1),
    );
    const totalActualRounds = this.getTotalRounds();

    if (req.skip) {
      this.state.completedRounds++;
      this.state.skippedRounds++;
      this.state.hasAnySkip = true;
      this.state.currentRound++;
      this.state.currentActualRound++;
    } else if (req.isCorrect) {
      this.state.successfulMoves += seqLength;
      this.state.completedRounds++;
      this.state.successfulRounds++;
      this.state.currentRound++;
      this.state.currentActualRound++;
    } else {
      this.state.completedRounds++;
      this.state.hasAnyPreviousWrong = true;
      this.state.currentRound++;
      this.state.currentActualRound++;
    }

    if (
      this.state.completedRounds >= totalActualRounds ||
      (!req.isCorrect && this.config.wrongMoveHandling === WRONG_MOVE_HANDLING.GAME_END)
    ) {
      // Handled later via gameOver call, but we can update status
    }

    const currentScore = calculateFinalScore(
      this.state.successfulMoves,
      this.config.scorePerClick,
      0,
    );

    return Promise.resolve({ score: currentScore, isFlagged: false });
  }

  public nextSequence(req: NextSequenceRequest): Promise<NextSequenceResponse> {
    if (!this.state) {
      return Promise.reject(new Error('No active session'));
    }

    const totalActualRounds = this.getTotalRounds();
    const nextActual = req.current_actual_round > 0 ? req.current_actual_round + 1 : 1;

    if (nextActual > totalActualRounds) {
      return Promise.reject(new Error('No more actual rounds'));
    }

    this.state.actualRoundRequested = Math.max(this.state.actualRoundRequested, nextActual);
    this.state.currentActualRound = nextActual;
    this.state.currentRound = nextActual;

    const activeInfo = this.getActiveLevelConfig(nextActual);
    const nextSeqLength = Math.min(
      activeInfo.config.maxSequence,
      activeInfo.config.minSequence + (activeInfo.relativeRound - 1),
    );
    const sequence = this.state.fullSequence.slice(0, nextSeqLength);

    const currentScore = calculateFinalScore(
      this.state.successfulMoves,
      this.config.scorePerClick,
      0,
    );

    return Promise.resolve({
      sequence,
      isDemo: false,
      isDemoMode: false,
      currentRound: nextActual,
      currentActualRound: nextActual,
      status: 'ROUND_SUCCESS',
      currentScore,
      gameSessionId: this.state.sessionId,
      endTime: this.state.enableTimer ? new Date(this.state.expiryAtMs).toISOString() : null,
    });
  }

  public prevSequence(req: NextSequenceRequest): Promise<NextSequenceResponse> {
    if (!this.state) {
      return Promise.reject(new Error('No active session'));
    }

    const prevActual = Math.max(1, req.current_actual_round - 1);
    const activeInfo = this.getActiveLevelConfig(prevActual);
    const prevSeqLength = Math.min(
      activeInfo.config.maxSequence,
      activeInfo.config.minSequence + (activeInfo.relativeRound - 1),
    );
    const sequence = this.state.fullSequence.slice(0, prevSeqLength);

    return Promise.resolve({
      sequence,
      isDemo: false,
      isDemoMode: false,
      currentRound: prevActual,
      currentActualRound: prevActual,
      status: 'ROUND_SUCCESS',
      currentScore: calculateFinalScore(this.state.successfulMoves, this.config.scorePerClick, 0),
      gameSessionId: this.state.sessionId,
      endTime: this.state.enableTimer ? new Date(this.state.expiryAtMs).toISOString() : null,
    });
  }

  public endGame(req: GameOverRequest): Promise<GameOverResponse> {
    if (!this.state) {
      return Promise.resolve({
        finalScore: 0,
        completedRounds: 0,
        successfulRounds: 0,
        bonus: 0,
        reason: req.reason,
      });
    }

    this.state.status =
      req.reason === 'TIME_UP' ? GAME_SESSION_STATUS.EXPIRED : GAME_SESSION_STATUS.ENDED;

    let timeBonus = 0;
    if (req.reason === 'COMPLETED') {
      const bonusRefMs = this.state.lastMoveAtMs ?? Date.now();
      const timeRemainingMs = Math.max(0, this.state.expiryAtMs - bonusRefMs);
      const isEligible = isBonusEligible(
        this.state.hasAnyPreviousWrong,
        this.config.wrongMoveHandling,
        this.state.hasAnySkip,
      );
      if (isEligible) {
        timeBonus = calculateTimeBonus(timeRemainingMs, this.config.bonusTimeRatio);
      }
    }

    const finalScore = calculateFinalScore(
      this.state.successfulMoves,
      this.config.scorePerClick,
      timeBonus,
    );

    return Promise.resolve({
      finalScore,
      completedRounds: this.state.completedRounds,
      successfulRounds: this.state.successfulRounds,
      bonus: timeBonus,
      reason: req.reason,
    });
  }

  public currentSession(): Promise<CurrentSessionResponse> {
    if (!this.state) {
      return Promise.reject(new Error('No active session'));
    }

    const currentScore = calculateFinalScore(
      this.state.successfulMoves,
      this.config.scorePerClick,
      0,
    );

    return Promise.resolve({
      sessionId: this.state.sessionId,
      status: this.state.status,
      startTime: new Date(this.state.startedAtMs).toISOString(),
      endTime: this.state.enableTimer ? new Date(this.state.expiryAtMs).toISOString() : null,
      finalScore: currentScore,
      completedActualRounds: this.state.completedRounds,
      completedRounds: this.state.completedRounds,
      successfulRounds: this.state.successfulRounds,
      successfulMoves: this.state.successfulMoves,
      roundProgress: 0,
      currentRound: this.state.currentRound,
      currentActualRound: this.state.currentActualRound,
      isDemoMode: false,
      currentScore,
      hasAnyPreviousWrong: this.state.hasAnyPreviousWrong,
      config: {
        timeLimit: this.config.timeLimit,
        minSequence: this.config.minSequence, // Original fallback
        maxSequence: this.config.maxSequence, // Original fallback
        enableDemo: this.config.enableDemo,
        demoMinSequence: this.config.demoMinSequence,
        demoMaxSequence: this.config.demoMaxSequence,
        flashDelay: this.config.flashDelay,
        bonusTimeRatio: this.config.bonusTimeRatio,
        scorePerClick: this.config.scorePerClick,
        cellCount: this.config.cellCount,
        wrongMoveHandling: this.config.wrongMoveHandling,
      },
    });
  }
}

export const gameEngine = new GameEngine();
